// src/pages/Admin/Users/AdherentManagementModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaSave,
  FaTimes,
  FaSearch,
  FaUserPlus,
  FaUserMinus,
  FaCheck,
} from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { getAllAdherentsList } from "../../../services/adherentServices";
import {
  assignAdherentToUser,
  removeAdherentFromUser,
  getAdherentsByUser, // ✅ utiliser cette API pour récupérer les adhérents liés
} from "../../../services/userServices";
import { toast } from "react-toastify";

const MAX_SUGGESTIONS = 50;

const formatGroups = (a) =>
  Array.isArray(a?.Groups) && a.Groups.length
    ? a.Groups.map((g) => g?.nom)
        .filter(Boolean)
        .join(", ")
    : "Sans groupe";

const ManageAdherentsModal = ({ isOpen, onClose, userId, refresh }) => {
  const [allAdherents, setAllAdherents] = useState([]);
  const [linkedList, setLinkedList] = useState([]); // liste brute renvoyée par getAdherentsByUser
  const [currentIds, setCurrentIds] = useState(new Set()); // état côté serveur (au moment de l’ouverture)
  const [selectedIds, setSelectedIds] = useState(new Set()); // état UI (éditable)
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const inputRef = useRef(null);

    const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  // Chargement: tous les adhérents + adhérents liés à l’utilisateur (pré-cochés)
  useEffect(() => {
    if (!isOpen || !userId) return;
    (async () => {
      try {
        setLoading(true);
        const [adherents, linked] = await Promise.all([
          getAllAdherentsList(),
          getAdherentsByUser(userId), // ← ✅
        ]);

        const safeAll = Array.isArray(adherents) ? adherents : [];
        const safeLinked = Array.isArray(linked) ? linked : [];

        setAllAdherents(safeAll);
        setLinkedList(safeLinked);

        const linkedIds = new Set(
          safeLinked
            .map((a) => a?.id)
            .filter((id) => id !== null && id !== undefined)
            .map(String)
        );

        setCurrentIds(linkedIds);
        setSelectedIds(new Set(linkedIds)); // ✅ pré-cocher
        setQuery("");
        setDropdownOpen(false);
        setHighlight(0);
      } catch (err) {
        console.error("Erreur chargement adhérents :", err);
        toast.error(
          "Erreur lors du chargement des adhérents de l'utilisateur."
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, userId]);

  // Candidats non sélectionnés + filtrage
  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    const selected = selectedIds;
    const base = allAdherents.filter((a) => !selected.has(String(a.id)));
    if (!q) return base.slice(0, MAX_SUGGESTIONS);
    const filtered = base.filter((a) =>
      `${a.nom || ""} ${a.prenom || ""}`.toLowerCase().includes(q)
    );
    return filtered.slice(0, MAX_SUGGESTIONS);
  }, [allAdherents, selectedIds, query]);

  // Adhérents sélectionnés (tags)
  const selectedAdherents = useMemo(() => {
    // Fusionne la source "allAdherents" avec "linkedList" (fallback si un id lié n’est pas dans la liste complète)
    const mapAll = new Map(allAdherents.map((a) => [String(a.id), a]));
    return [...selectedIds]
      .map(
        (sid) => mapAll.get(sid) || linkedList.find((x) => String(x.id) === sid)
      )
      .filter(Boolean)
      .sort((a, b) =>
        `${a.nom || ""} ${a.prenom || ""}`.localeCompare(
          `${b.nom || ""} ${b.prenom || ""}`,
          "fr"
        )
      );
  }, [allAdherents, selectedIds, linkedList]);

  const addAdherent = (id) => {
    setSelectedIds((prev) => new Set(prev).add(String(id)));
    setQuery("");
    setDropdownOpen(true);
    setHighlight(0);
    inputRef.current?.focus();
  };

  const removeAdherent = (id) => {
    setSelectedIds((prev) => {
      const s = new Set(prev);
      s.delete(String(id));
      return s;
    });
  };

  const handleKeyDown = (e) => {
    if (!dropdownOpen || candidates.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, candidates.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const choice = candidates[highlight];
      if (choice) addAdherent(choice.id);
    } else if (e.key === "Escape") {
      setDropdownOpen(false);
    }
  };

  // Enregistrer (diff add/remove)
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    setSubmitting(true);
    try {
      const cur = currentIds;
      const sel = selectedIds;

      const toAdd = [...sel]
        .filter((id) => !cur.has(id))
        .map((id) => Number(id));
      const toRemove = [...cur]
        .filter((id) => !sel.has(id))
        .map((id) => Number(id));

      await Promise.all([
        ...toAdd.map((aid) => assignAdherentToUser(userId, aid)), // ✅ ajoute
        ...toRemove.map((aid) => removeAdherentFromUser(userId, aid)), // ✅ retire
      ]);

      toast.success(
        `Liens mis à jour (${toAdd.length} ajout(s), ${toRemove.length} retrait(s)).`
      );
      await refresh?.();
      onClose?.();
    } catch (err) {
      console.error("Erreur mise à jour adhérents ↔ utilisateur :", err);
      toast.error("Erreur lors de l’enregistrement des liaisons.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal */}
      <div className="relative w-full max-w-3xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
        {/* Header dégradé */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
            Gérer les adhérents liés
          </h3>
          <p className="text-white/90 text-center text-xs mt-1">
            Ajoutez via la liste recherchable, retirez depuis la sélection
            ci-dessous.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-5 sm:p-6 space-y-5 max-h-[70vh] overflow-auto">
            {/* Sélecteur recherchable */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100">
                Ajouter un adhérent
              </label>
              <div className="relative">
                <div className="flex items-center gap-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 focus-within:ring-2 focus-within:ring-violet-400">
                  <FaSearch className="opacity-60" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setDropdownOpen(true);
                      setHighlight(0);
                    }}
                    onFocus={() => setDropdownOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder="Nom ou prénom…"
                    className="w-full bg-transparent outline-none text-slate-900 dark:text-slate-100"
                  />
                </div>

                {/* Dropdown résultats */}
                {dropdownOpen && (
                  <div
                    className="absolute z-10 mt-2 w-full max-h-72 overflow-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg"
                    onMouseDown={(e) => e.preventDefault()} // garder le focus
                  >
                    {loading ? (
                      <div className="p-3 text-sm text-slate-500">
                        Chargement…
                      </div>
                    ) : candidates.length === 0 ? (
                      <div className="p-3 text-sm text-slate-500">
                        Aucun résultat. Tapez un nom/prénom.
                      </div>
                    ) : (
                      candidates.map((a, i) => (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => addAdherent(a.id)}
                          className={`w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800 ${
                            i === highlight
                              ? "bg-slate-50 dark:bg-slate-800"
                              : ""
                          }`}
                          title={`${a.nom} ${a.prenom}`}
                        >
                          <img
                            src={toAbsolute(a.photo_url) || "/img/user_avatar.jpg"}
                            alt=""
                            className="h-8 w-8 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                          />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                              {a.nom} {a.prenom}
                            </div>
                            <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                              {formatGroups(a)}
                            </div>
                          </div>
                          <div className="ml-auto text-xs inline-flex items-center gap-1 rounded px-2 py-0.5 ring-1 ring-emerald-300/50 text-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 dark:text-emerald-200">
                            <FaUserPlus /> Ajouter
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Sélection actuelle */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100">
                  Adhérents liés ({selectedAdherents.length})
                </label>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  Clic sur ✕ pour retirer
                </div>
              </div>

              {selectedAdherents.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-4 text-sm text-slate-500 dark:text-slate-400">
                  Aucun adhérent sélectionné.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {selectedAdherents.map((a) => (
                    <div
                      key={a.id}
                      className="group inline-flex items-center gap-2 rounded-full bg-violet-50 dark:bg-violet-950/30 ring-1 ring-violet-200/70 dark:ring-violet-800/60 px-3 py-1"
                      title={`${a.nom} ${a.prenom} — ${formatGroups(a)}`}
                    >
                      <FaCheck className="text-violet-600 dark:text-violet-300" />
                      <img
                        src={toAbsolute(a.photo_url) || "/img/user_avatar.jpg"}
                        alt=""
                        className="h-5 w-5 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                      />
                      <span className="text-xs font-medium text-slate-900 dark:text-slate-100">
                        {a.nom} {a.prenom}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeAdherent(a.id)}
                        className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-rose-100 text-rose-700 hover:bg-rose-200 dark:bg-rose-900/30 dark:text-rose-200"
                        title="Retirer"
                      >
                        <FaUserMinus size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 px-5 pb-5">
            <Button
              text="Fermer"
              icon={FaTimes}
              type="button"
              onClick={onClose}
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
            />
            <Button
              text={submitting ? "Enregistrement…" : "Enregistrer"}
              icon={FaSave}
              type="submit"
              disabled={submitting || loading}
              className={`!rounded-xl gap-2 ${
                submitting ? "opacity-60 pointer-events-none" : ""
              }`}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManageAdherentsModal;
